import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { DashboardPeriodState } from "@/utils/dashboardPeriodUtils";

const STORAGE_KEY = "tellerwand_dashboard_period";

const DEFAULT_PERIOD: DashboardPeriodState = { period: "all-time" };

/**
 * Load Stored Period.
 */
function loadStoredPeriod(): DashboardPeriodState {
  if (typeof window === "undefined") {
    return DEFAULT_PERIOD;
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_PERIOD;
    }
    const parsed = JSON.parse(raw) as DashboardPeriodState;
    if (
      parsed?.period === "all-time" ||
      parsed?.period === "this-month" ||
      parsed?.period === "custom"
    ) {
      return parsed;
    }
  } catch {
    // ignore corrupt storage
  }
  return DEFAULT_PERIOD;
}

interface DashboardPeriodContextValue {
  periodState: DashboardPeriodState;
  setPeriodState: (next: DashboardPeriodState) => void;
}

const DashboardPeriodContext = createContext<DashboardPeriodContextValue | null>(null);

/**
 * Dashboard Period Provider.
 */
export function DashboardPeriodProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [periodState, setPeriodState] = useState<DashboardPeriodState>(loadStoredPeriod);

  const persistPeriodState = useCallback((next: DashboardPeriodState) => {
    setPeriodState(next);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // quota / private mode
    }
  }, []);

  const value = useMemo(
    () => ({ periodState, setPeriodState: persistPeriodState }),
    [periodState, persistPeriodState]
  );

  return (
    <DashboardPeriodContext.Provider value={value}>{children}</DashboardPeriodContext.Provider>
  );
}

export function useDashboardPeriod(): DashboardPeriodContextValue {
  const ctx = useContext(DashboardPeriodContext);
  if (!ctx) {
    throw new Error("useDashboardPeriod must be used within DashboardPeriodProvider");
  }
  return ctx;
}
